import React from 'react';
import { connect } from 'react-redux';
import { get, template } from 'lodash';
import BuildView from '../../views/buildView/BuildView';
import OptionsPage from '../../common/OptionsPage';
import SimpleOptionGroup from '../../common/options/SimpleOptionGroup';
import OptionsAccordionHeader from '../../common/options/OptionsAccordionHeader';
import ExteriorCgi from '../../components/exteriorCgi/ExteriorCgi';
import {
  Accordion,
  AccordionGroup,
  AccordionContent,
} from 'core/controls/accordion/Accordion';
import ColorOptionStrip from '../../common/options/colorOptionStrip/ColorOptionStrip';
import { combineClassNames } from '../../../core/css';
import {
  computeDefaultAngle,
  computeImageSize,
} from '../../common/cgi/cgiHelpers';
import { cgiDimensions } from '../../common/cgi/Cgi';
import { useNavigate } from 'react-router-dom';
import { EXTERIOR } from '../../common/steps';

const ColorOptionGroup = ({
  header,
  options,
  id,
  onChange,
  onDetails,
  displayOnly,
  isOpenDefault,
  isUpdating,
  selectingOption,
  optionToggled,
}) => {
  const count = options.length;

  if (count === 0) {
    return null;
  }

  const ColorStrips = ({ analyticsKey }) =>
    options.map((option) => (
      <ColorOptionStrip
        option={option}
        onChange={onChange}
        onDetails={onDetails}
        analyticsKey={analyticsKey}
        key={option.code}
        isUpdating={isUpdating}
        isUpdatingOption={
          isUpdating && get(option, 'code') === get(selectingOption, 'code')
        }
        optionToggled={optionToggled}
      />
    ));

  const selectedOption = options.filter((option) => option.selected);

  return (
    <AccordionGroup
      id={id}
      displayOnly={displayOnly}
      isOpenDefault={isOpenDefault}
      className="accordion-item--simple-option">
      <OptionsAccordionHeader
        className="accordion-title--simple-option"
        header={header}
        count={count}
        analyticsKey="right rail"
      />
      <AccordionContent className="accordion-content--color">
        <div
          role="radiogroup"
          className={combineClassNames(
            'exterior-color-container',
            'stat-wrapper'
          )}
          data-dtm={header}
          data-dtm2={selectedOption[0]?.description}>
          <ColorStrips analyticsKey={header} />
        </div>
      </AccordionContent>
    </AccordionGroup>
  );
};

const Colors = ({
  exterior,
  cgiSize,
  bottomOffset,
  optionToggled,
  breakpoint,
  exteriorAngles,
  interiorAngles,
  wheelAngles,
  moreViewsPath,
}) => {
  const categories = get(exterior, 'categories');
  if (!categories || categories.length < 1) {
    return null;
  }

  const { cgiImageUrl } = exterior;

  const imageSize = computeImageSize(breakpoint, cgiSize);
  const angle = computeDefaultAngle(
    'default',
    exteriorAngles,
    interiorAngles,
    wheelAngles,
    false
  );

  const cgi = {
    url: template(cgiImageUrl)({
      imageSize,
      degree: angle,
      removeCat: '',
    }),
    dimensions: cgiDimensions[imageSize],
  };

  const isDisplayOnly = categories.length === 1;

  const colorStripKeys = ['exterior.color', 'most.popular.exterior.color'];

  const getOptions = () => {
    return (
      <OptionsPage>
        {({ onChange, onDetails, isUpdating, selectingOption }) => (
          <Accordion>
            <>
              {categories.map((category, index) =>
                colorStripKeys.includes(category.key) ? (
                  <ColorOptionGroup
                    key={category.key}
                    header={category.label}
                    options={category.options}
                    analyticsKey="right rail"
                    id="exterior-colors"
                    displayOnly={index === 0 ? isDisplayOnly : false}
                    onChange={onChange}
                    onDetails={onDetails}
                    isOpenDefault={category.expanded}
                    isUpdating={isUpdating}
                    selectingOption={selectingOption}
                    optionToggled={optionToggled}
                  />
                ) : (
                  <SimpleOptionGroup
                    key={category.key}
                    header={category.label}
                    options={category.options}
                    analyticsKey={category.label}
                    id={category.key}
                    onChange={onChange}
                    onDetails={onDetails}
                    displayOnly={index === 0 ? isDisplayOnly : false}
                    isOpenDefault={category.expanded}
                    isUpdating={isUpdating}
                    selectingOption={selectingOption}
                  />
                )
              )}
            </>
          </Accordion>
        )}
      </OptionsPage>
    );
  };

  const navigate = useNavigate();

  const onClick = () => {
    navigate(`${moreViewsPath?.pathname}${moreViewsPath?.search}`);
  };

  return (
    <BuildView
      onload={() => {}}
      bottomOffset={bottomOffset}
      cgi={<ExteriorCgi cgi={cgi} onClick={onClick} />}
      options={getOptions()}
      step={EXTERIOR}
    />
  );
};

const mapStateToProps = (state) => {
  const { config, optionToggled, breakpoint } = state;
  const { exterior } = config;
  const {
    cgiSize,
    exteriorAngles,
    interiorAngles,
    wheelAngles,
  } = state.appResources;
  return {
    optionToggled,
    exterior,
    cgiSize,
    breakpoint,
    exteriorAngles,
    interiorAngles,
    wheelAngles,
  };
};

export default connect(mapStateToProps)(Colors);
